import useResize from 'hooks/useResize';
import useScroll from 'hooks/useScroll';
import { useEffect, useRef, useState } from 'react';
import useModifiers from 'hooks/useModifiers';

export enum TooltipModifier {
  OVERVIEW = 'overview',
  VALIDATION = 'validation',
}
export enum TooltipAlignEnum {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

export enum TooltipCursorEnum {
  HELP = 'help',
  POINTER = 'pointer',
}

export interface TooltipPos {
  left: string;
  right: string;
  top: string;
}

export interface TooltipArrowPos {
  left: string;
  top: string;
}

export interface TooltipProps {
  children: React.ReactElement;
  title?: string;
  message?: string;
  tooltipChildren?: React.ReactElement;
  align: TooltipAlignEnum;
  cursor?: TooltipCursorEnum;
  modifiers: TooltipModifier[];
}

function Tooltip(props: TooltipProps) {
  const { windowWidth, windowHeight } = useResize();
  const { scrollY } = useScroll(15);
  const [isActive, setIsActive] = useState(false);
  const [arrowPos, setArrowPos] = useState<TooltipArrowPos | undefined>(undefined);
  const {
    children,
    title,
    message,
    align,
    tooltipChildren,
    cursor,
  } = props;
  const sourceRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const arenaRef = useRef<HTMLDivElement>(null);
  const mods = useModifiers(props.modifiers);

  const computePosition = (
    source: HTMLDivElement,
    content: HTMLDivElement,
    arena: HTMLDivElement,
  ): TooltipPos => {
    const sBbox = source.getBoundingClientRect();
    const cBbox = content.getBoundingClientRect();
    const aBbox = arena.getBoundingClientRect();

    const areaCenter = Math.floor(aBbox.width / 2);
    const sourceCenter = sBbox.left + Math.floor(sBbox.width / 2);

    const sourceIsAtRight = sourceCenter >= areaCenter;
    const sourceIsAtLeft = sourceCenter < areaCenter;

    const exceedsAreaAtRight = sourceCenter + (cBbox.width / 2) >= aBbox.width;
    const exceedsAreaAtLeft = sourceCenter - (cBbox.width / 2) <= 0;

    setArrowPos({ left: `${sourceCenter}px`, top: `${sBbox.top + sBbox.height}px` });

    const offset = 25;

    if (sourceIsAtRight && exceedsAreaAtRight) {
      return {
        left: 'auto',
        right: '0px',
        top: `${sBbox.top + sBbox.height}px`,
      };
    }

    if (sourceIsAtLeft && exceedsAreaAtLeft) {
      return {
        left: '0px',
        right: 'auto',
        top: `${sBbox.top + sBbox.height}px`,
      };
    }

    if (align === TooltipAlignEnum.LEFT) {
      return {
        left: `${sBbox.left - offset}px`,
        right: 'auto',
        top: `${sBbox.top + sBbox.height}px`,
      };
    }

    if (align === TooltipAlignEnum.CENTER) {
      return {
        left: `${sBbox.left + (sBbox.width / 2) - (cBbox.width / 2)}px`,
        right: 'auto',
        top: `${sBbox.top + sBbox.height}px`,
      };
    }

    if (align === TooltipAlignEnum.RIGHT) {
      return {
        left: `${sBbox.left + sBbox.width - cBbox.width + offset}px`,
        right: 'auto',
        top: `${sBbox.top + sBbox.height}px`,
      };
    }

    // default at center
    return {
      left: `${sBbox.left + (sBbox.width / 2) - (cBbox.width / 2)}px`,
      right: 'auto',
      top: `${sBbox.top + sBbox.height}px`,
    };
  };

  useEffect(() => {
    if (isActive && sourceRef.current && contentRef.current && arenaRef.current) {
      const sourceEl = sourceRef.current;
      const contentEl = contentRef.current;
      const arenaEl = arenaRef.current;

      const positions = computePosition(sourceEl, contentEl, arenaEl);

      Object.assign(contentEl.style, { ...positions });
    }
  }, [windowHeight, windowWidth, isActive]);

  useEffect(() => {
    setIsActive(false);
  }, [scrollY]);

  let cursorClass = 'cursor-pointer'; // default

  if (cursor === TooltipCursorEnum.POINTER) {
    cursorClass = 'cursor-pointer';
  } else if (cursor === TooltipCursorEnum.HELP) {
    cursorClass = 'cursor-help';
  }

  return (
    <div className={`ct_ttip _${align} ${mods}`} onMouseLeave={() => setIsActive(false)}>
      <div
        ref={sourceRef}
        className={`ct_ttip-source _${cursorClass} ${mods}`}
        onMouseEnter={() => setIsActive(true)}
      >
        {children}
      </div>
      {!!isActive && (
        <div ref={arenaRef} className="ct_ttip-arena">
          <div ref={contentRef} className="ct_ttip-content">
            <div className="ct_ttip-tip">
              <div
                className="ct_ttip-arrow"
                style={{
                  '--ttip-arrow-left': arrowPos?.left || '50%',
                  '--ttip-arrow-top': arrowPos?.top || '50%',
                } as React.CSSProperties}
              />
              <div className="ct_ttip-title">{title}</div>
              {message && (
                <div className="ct_ttip-message">{message}</div>
              )}
              {tooltipChildren && (tooltipChildren)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

Tooltip.defaultProps = { ...{ } };

export default Tooltip;
